import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#0A101D] border border-[#D4AF37]/20 px-4 py-3.5 text-sm text-slate-200 placeholder:text-slate-600 focus:border-[#D4AF37]/60 focus:outline-none transition-colors rounded-sm";

export const Contact = ({ t, lang }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-28 bg-[#111A2E]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <p className="text-xs tracking-[0.35em] uppercase font-mono text-[#E2C376] mb-5 flex items-center gap-3">
            <span className="w-10 h-px bg-[#D4AF37]" /> {t.contact.eyebrow}
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-slate-50 tracking-tight mb-6">{t.contact.title}</h2>
          <p className="text-base text-slate-400 leading-relaxed mb-10">{t.contact.sub}</p>

          <div className="space-y-4">
            <div className="flex items-start gap-4 border border-[#D4AF37]/20 p-6 rounded-sm">
              <MapPin className="w-5 h-5 text-[#D4AF37] mt-1" strokeWidth={1.4} />
              <div>
                <div className="text-sm font-mono uppercase tracking-widest text-slate-500 mb-2">{t.contact.address_title}</div>
                <div className="text-slate-200 text-sm leading-relaxed">
                  Risc Goup Suisse SA<br />Route de Champ-Colin 12<br />1260 Nyon, Suisse
                </div>
              </div>
            </div>
            <div className="flex items-start gap-4 border border-[#D4AF37]/20 p-6 rounded-sm">
              <Mail className="w-5 h-5 text-[#D4AF37] mt-1" strokeWidth={1.4} />
              <div>
                <div className="text-sm font-mono uppercase tracking-widest text-slate-500 mb-2">E-mail</div>
                <a data-testid="contact-email-link" href="mailto:info@rgsuisse.com" className="text-slate-200 text-sm hover:text-[#E5C365] transition-colors">
                  info@rgsuisse.com
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5"
        >
          <input data-testid="contact-input-name" required placeholder={t.contact.name} value={form.name} onChange={set("name")} className={inputCls} />
          <input data-testid="contact-input-email" required type="email" placeholder={t.contact.email} value={form.email} onChange={set("email")} className={inputCls} />
          <input data-testid="contact-input-phone" placeholder={t.contact.phone} value={form.phone} onChange={set("phone")} className={inputCls} />
          <input data-testid="contact-input-company" placeholder={t.contact.company} value={form.company} onChange={set("company")} className={inputCls} />
          <select data-testid="contact-select-service" value={form.service} onChange={set("service")} className={`${inputCls} sm:col-span-2 appearance-none`}>
            <option value="">{t.contact.service_placeholder}</option>
            {t.contact.services_list.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <textarea
            data-testid="contact-input-message"
            required
            rows={5}
            placeholder={t.contact.message}
            value={form.message}
            onChange={set("message")}
            className={`${inputCls} sm:col-span-2 resize-none`}
          />
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={sending}
            className="sm:col-span-2 px-8 py-4 bg-[#D4AF37] text-[#0A101D] font-semibold tracking-wide hover:bg-[#E5C365] transition-all disabled:opacity-60 rounded-sm"
          >
            {sending ? t.contact.sending : t.contact.submit}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
